import React, { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';

import { StoreContext } from 'store';
import { MARKET_URL } from '../../../consts/market';
import { useMarketBannersContext } from '../../ContextBanners';
import { Icon } from '../../Icons';
import { Button } from '../../Button';

import './MarketBanner.scss';
import { ButtonColor, ButtonSize } from 'components/Button/Button';
import { trackEvent } from 'analytics/amplitude';
import { Text } from 'components/Text';
import { isStaging } from 'helpers';

type BannerType =
  | 'common'
  | 'halloween'
  | 'blackFriday'
  | 'christmas'
  | 'football'
  | 'grand';

type BannerPosition = 'top' | 'bottom';

const images: Record<BannerType, string> = {
  common: '/img/common/market-banner-batya.webp',
  halloween: '/img/common/market-banner-halloween.webp',
  blackFriday: '/img/common/market-banner-batya.webp',
  christmas: '/img/common/market-banner-christmas.webp',
  football: '/img/common/market-banner-football.webp',
  grand: '/img/common/market-banner-football.webp',
};

const bgImages: Partial<Record<BannerType, string>> = {
  christmas: '/img/common/market-banner-christmas-bg.webp',
  football: '/img/common/market-banner-battle-royale-bg.webp',
  grand: '/img/common/market-banner-grand.webp',
};

const getBannerBgStyle = (type: BannerType, position: BannerPosition) => {
  if (!bgImages[type]) {
    return undefined;
  }

  if (position === 'bottom') {
    return { backgroundImage: `url(${bgImages[type]})` };
  }

  if (position === 'top' && type === 'football') {
    return { backgroundImage: `url(${bgImages[type]})` };
  }

  return undefined;
};

const buttonColor: Record<BannerType, ButtonColor> = {
  common: 'red',
  halloween: 'yellow',
  blackFriday: 'red',
  christmas: 'red',
  football: 'red',
  grand: 'red',
};
const buttonSize: Record<BannerPosition, ButtonSize> = {
  bottom: 'm',
  top: 's',
};
const marketUrlParams: Record<BannerPosition, string> = {
  bottom: '&utm_medium=banner&utm_campaign=darkFriday&utm_term=bottom',
  top: '&utm_medium=banner&utm_campaign=darkFriday&utm_term=top',
};

interface MarketBannerProps {
  position: BannerPosition;
}

export const MarketBanner: React.FC<MarketBannerProps> = ({ position }) => {
  const { hideMainMarketBanner, hideAll } = useMarketBannersContext();
  const hideBannerVisible = hideMainMarketBanner || hideAll;
  const location = useLocation();

  const type: BannerType = useMemo<BannerType>(() => {
    const currentDate = new Date();
    const currentUtcDate = new Date(currentDate.toUTCString());
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();
    // if date is between 18.12.23 — 08.01.24 then show christmas banner
    if (
      (currentMonth === 11 && currentDay >= 18) ||
      (currentMonth === 0 && currentDay <= 8)
    ) {
      return 'christmas';
    }
    // if date is between 25.04.24 — 15.05.24 then show football24 banner
    if (
      (currentMonth === 3 && currentDay >= 25) ||
      (currentMonth === 4 && currentDay <= 15)
    ) {
      return 'football';
    }

    // if date is between 08.07.24 10:00 — 29.07.24 10:00 then show football24 banner
    if (
      currentUtcDate >= new Date(Date.UTC(2024, 6, 8, 10, 0, 0)) &&
      currentUtcDate <= new Date(Date.UTC(2024, 6, 29, 10, 0, 0))
      // ||
      // isStaging()
    ) {
      return 'football';
    }
    // if date is between 12.08.24 10:00 — 01.09.24 10:00 then show grand banner
    if (
      currentUtcDate >= new Date(Date.UTC(2024, 7, 12, 10, 0, 0)) &&
      currentUtcDate <= new Date(Date.UTC(2024, 8, 1, 10, 0, 0))
    ) {
      return 'grand';
    }

    // if date is between 09.09.24 10:00 — 29.09.24 10:00 then show grand banner
    if (
      currentUtcDate >= new Date(Date.UTC(2024, 8, 9, 10, 0, 0)) &&
      currentUtcDate <= new Date(Date.UTC(2024, 8, 29, 10, 0, 0))
    ) {
      return 'football';
    }

    return 'blackFriday';
  }, []);

  const {
    localisation: [locale],
    translation: t,
  } = useContext(StoreContext);

  const marketUrl = MARKET_URL[locale || 'en'];

  const renderRibbon = () => {
    if (
      type !== 'blackFriday' &&
      type !== 'christmas' &&
      type !== 'football' &&
      type !== 'grand'
    ) {
      return null;
    }

    return (
      <div
        className={cx('market-banner__ribbon', `market-banner__ribbon--${position}`, {
          'market-banner__ribbon--black-friday': type === 'blackFriday',
          'market-banner__ribbon--christmas': type === 'christmas',
          'market-banner__ribbon--football': type === 'football',
          'market-banner__ribbon--grand': type === 'grand',
        })}
      />
    );
  };

  if (hideBannerVisible) {
    return null;
  }

  if (location.pathname === '/market' || location.pathname?.includes('/quiz')) {
    return null;
  }

  return (
    <div
      className={cx('market-banner__wrapper', {
        'market-banner__wrapper--top': position === 'top',
        'market-banner__wrapper--bottom': position === 'bottom',
        'market-banner__wrapper--search-page': location.pathname === '/',
      })}
    >
      {renderRibbon()}

      <div
        className={cx('market-banner', {
          'market-banner--common': type === 'common',
          'market-banner--halloween': type === 'halloween',
          'market-banner--black-friday': type === 'blackFriday',
          'market-banner--christmas': type === 'christmas',
          'market-banner--football': type === 'football',
          'market-banner--grand': type === 'grand',
          'market-banner--top': position === 'top',
          'market-banner--bottom': position === 'bottom',
        })}
        style={getBannerBgStyle(type, position)}
      >
        {type === 'christmas' && position === 'bottom' && (
          <div className="market-banner__shadow" />
        )}
        <img
          className="market-banner__image"
          src={images[type]}
          alt="Market banner img"
        />

        <div className="market-banner__content">
          <Text.HeaderL className="market-banner__title">
            {t.footer.marketBanner.title}
          </Text.HeaderL>
          <a
            target="_blank"
            // href={marketUrl}
            href={marketUrl + marketUrlParams[position]}
            rel="noreferrer"
            className="market-banner__button-link"
          >
            <Button
              size={buttonSize[position]}
              withTriangle
              labelClassName="market-banner__button-label"
              Icon={Icon.MarketCart}
              color={buttonColor[type]}
              onClick={() => {
                trackEvent('market_banner_click', { position });
              }}
            >
              {t.footer.marketBanner.buttonText}
            </Button>
          </a>
        </div>
      </div>
      {renderRibbon()}
    </div>
  );
};
